import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  AuthService,
  AuthorizationService,
} from '@nimmsta/ng-commons-lib/auth';
import { AppDialogService } from '@nimmsta/ng-commons-lib/services/app-dialog';
import { BatteryPredictionBadgeComponent } from '@nimmsta/ng-commons-lib/battery-prediction-badge';
import {
  Column,
  CustomAction,
  DataTableComponent,
  EmptyTableComponent,
  NimmstaDataSource,
} from '@nimmsta/ng-commons-lib/data-table';
import {
  ConfirmationDialogComponent,
  createDeviceStatusConfirmationDetails,
} from '@nimmsta/ng-commons-lib/confirmation-dialog';
import {
  DateTimePipe,
  NimmstaTimeAgoPipe,
} from '@nimmsta/ng-commons-lib/pipes';
import { DeviceStatus, UserRole } from '@nimmsta/ng-commons-lib/models';
import { DeviceStatusBadgeComponent } from '@nimmsta/ng-commons-lib/device-status-badge';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { take } from 'rxjs';
import { LightTag } from '../../models/lighttag';
import { LightTagService } from '../../services/lighttag.service';
import { TranslatePipe } from '@ngx-translate/core';
import { LightTagTypePipe } from './lighttag-type.pipe';
import { createOrganizationFilter } from '../organization-filter.util';

_('lighttag.title');

@Component({
  templateUrl: 'lighttag.component.html',
  selector: 'app-lighttag',
  imports: [
    DataTableComponent,
    EmptyTableComponent,
    ErrorComponent,
    TranslatePipe,
    BatteryPredictionBadgeComponent,
    DeviceStatusBadgeComponent,
  ],
  providers: [NimmstaTimeAgoPipe, DateTimePipe, LightTagTypePipe],
})
export class LightTagComponent implements OnInit, OnChanges {
  private lightTagService = inject(LightTagService);
  private authService = inject(AuthService);
  private authorizationService = inject(AuthorizationService);
  private modalService = inject(AppDialogService);
  private router = inject(Router);
  private timeago = inject(NimmstaTimeAgoPipe);
  private dateTimePipe = inject(DateTimePipe);
  private lightTagTypePipe = inject(LightTagTypePipe);

  lightTagSource!: NimmstaDataSource<LightTag>;

  @Input() organizationId?: string;

  error?: HttpErrorResponse;

  isAdmin = false;

  protected isManager = false;

  statusActions: CustomAction<LightTag>[] = [];

  protected readonly compactPortraitColumns = [
    'lastLocation',
    'uniqueIdentifier',
  ];

  @ViewChild('batteryPredictionCell', { static: true })
  private batteryPredictionCell!: TemplateRef<{ $implicit: LightTag }>;

  @ViewChild('statusCell', { static: true })
  private statusCell!: TemplateRef<{ $implicit: LightTag }>;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent?: DataTableComponent<LightTag>;

  get columns(): Column[] {
    const columns = [
      new Column('uniqueIdentifier', 'lighttag.serialNr', { sortable: true }),
      new Column('status', 'lighttag.status', {
        template: this.statusCell,
        sortable: true,
      }),
      new Column('isListening', 'lighttag.listeningType', {
        pipe: this.lightTagTypePipe,
        sortable: true,
      }),
      new Column('lastLocation', 'lighttag.lastLocation', { sortable: true }),
      new Column('lastSeen', 'lighttag.lastSeen', {
        pipe: this.timeago,
        sortable: true,
        invertSorting: true,
      }),
      new Column('lastIlluminated', 'lighttag.lastIlluminated', {
        pipe: this.timeago,
        sortable: true,
      }),
      new Column('warrantyEndDate', 'lighttag.warrantyEndDate', {
        pipe: this.dateTimePipe,
        sortable: false,
      }),
    ];

    if (this.isAdmin) {
      columns.splice(
        5,
        0,
        new Column('batteryPrediction', 'batteryPrediction.status', {
          template: this.batteryPredictionCell,
        }),
      );
    }

    return columns;
  }

  ngOnInit(): void {
    this.lightTagSource = new NimmstaDataSource<LightTag>(this.lightTagService);
    this.statusActions = [
      this.createStatusAction('lighttag.markBroken', DeviceStatus.BROKEN),
      this.createStatusAction('lighttag.markMissing', DeviceStatus.MISSING),
    ];
    this.applyOrganizationFilter();
    this.authService
      .getCurrentUser()
      .pipe(take(1))
      .subscribe((user) => {
        this.isAdmin = user.role === UserRole.ADMIN;
        this.isManager = user.role === UserRole.MANAGER;
      });
  }

  private createStatusAction(
    text: string,
    status: DeviceStatus,
  ): CustomAction<LightTag> {
    const action = new CustomAction<LightTag>(
      text,
      false,
      false,
      (id) => this.findLightTag(id)?.status !== status,
    );
    action.isEnabled = () =>
      this.authorizationService.matchesCurrentUser({
        anyRoles: [UserRole.ADMIN, UserRole.MANAGER],
      });
    action.subscribe((id) => this.confirmStatusUpdate(id, status));
    return action;
  }

  private confirmStatusUpdate(id: string, status: DeviceStatus): void {
    const device = this.findLightTag(id);
    if (!device || device.status === status) {
      return;
    }

    const modalRef = this.modalService.open<
      ConfirmationDialogComponent,
      unknown,
      boolean
    >(ConfirmationDialogComponent, {
      data: {
        title: 'lighttag.statusUpdateConfirmTitle',
        message: 'lighttag.statusUpdateConfirmMessage',
        itemLabel: 'lighttag.serialNr',
        itemLabelTranslate: true,
        itemValue: device.uniqueIdentifier,
        details: createDeviceStatusConfirmationDetails(
          'lighttag',
          'lighttag.statusValues',
          device.status,
          status,
          'lighttag.lastLocation',
          device.lastLocation,
        ),
        cancelLabel: 'cancel',
        confirmLabel: 'lighttag.statusUpdateConfirm',
        confirmButtonClass:
          status === DeviceStatus.BROKEN ? 'btn-danger' : 'btn-warning',
      },
    });

    modalRef.closed.pipe(take(1)).subscribe((confirmed) => {
      if (confirmed === true) {
        this.updateStatus(id, status);
      }
    });
  }

  private updateStatus(id: string, status: DeviceStatus): void {
    this.lightTagService.updateStatus(id, status).subscribe({
      next: () => this.tableComponent?.loadDataPage(),
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  private findLightTag(id: string): LightTag | undefined {
    return this.lightTagSource.data?.content?.find(
      (device) => device.id === id,
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !changes['organizationId'] ||
      changes['organizationId'].firstChange ||
      !this.lightTagSource
    ) {
      return;
    }

    this.applyOrganizationFilter();
    this.tableComponent?.loadDataPage(true);
  }

  private applyOrganizationFilter(): void {
    this.lightTagSource.defaultFilter = createOrganizationFilter(
      'organizationId',
      this.organizationId,
    );
  }

  show(id: string): void {
    this.router.navigate(['/devices/light-tags', id]);
  }
}
